import { Injectable } from '@angular/core';
import { ObservableCursor } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  shareReplay,
  take,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { PreviousJobs } from 'server/shared/collections/previousJobs';
import { Users } from 'server/shared/collections/users';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { CustomerService } from '../customer.service';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';

@Injectable({
  providedIn: 'root',
})
export class PoolerService {
  trackers: Record<string, Tracker.Computation | false> = {
    sharedPoolCustomers: false,
    poolers: false,
  };
  sharedPoolCustomers$ = new BehaviorSubject<Customer[]>([]);
  readySharedPoolCustomers$ = this.sharedPoolCustomers$.pipe(
    filter((customers): boolean => !!customers),
  );
  jobSharedPoolCustomers$ = new BehaviorSubject<Customer[]>([]);
  readyJobSharedPoolCustomers$ = this.jobSharedPoolCustomers$.pipe(
    filter((customers): boolean => !!customers),
  );
  employmentSharedPoolCustomers$ = new BehaviorSubject<Customer[]>([]);
  readyEmploymentSharedPoolCustomers$ =
    this.employmentSharedPoolCustomers$.pipe(
      filter((customers): boolean => !!customers),
    );

  // map of userId to customer which prioritizes own customer over shared pool customer
  // and allows access to customer without having to loop through userIds
  poolerCustomerMap$ = new BehaviorSubject<PoolerCustomerMap | undefined>(
    undefined,
  );
  employmentPoolerCustomerMap$ = new BehaviorSubject<
    PoolerCustomerMap | undefined
  >(undefined);
  readyPoolerCustomerMap$ = this.poolerCustomerMap$.pipe(
    filter(filterIsNotEmpty),
  );
  readyEmploymentPoolerCustomerMap$ = this.employmentPoolerCustomerMap$.pipe(
    filter(filterIsNotEmpty),
  );

  poolerUsers$ = this.getPoolers({}).pipe(debounceTime(100), shareReplay(1));

  poolerUsersById$ = this.poolerUsers$.pipe(
    map((users) => new Map(users.map((user) => [user._id, user]))),
    shareReplay(1),
  );

  previousJobs$ = observeTracker(() => {
    return PreviousJobs.find({}).fetch();
  });

  previousJobsByUserId$ = this.previousJobs$.pipe(
    map(
      (previousJobs) =>
        new Map(
          previousJobs.map((previousJob) => [previousJob.userId, previousJob]),
        ),
    ),
    shareReplay(1),
  );

  constructor(private customerService: CustomerService) {
    this.subscribeSharedPoolCustomers();
    this.subscribePoolers();
  }

  async getCustomerOfPooler(userId: string): Promise<Customer | undefined> {
    return new Promise((resolve, reject) => {
      this.readyPoolerCustomerMap$.pipe(take(1)).subscribe((poolerMap) => {
        const customerOfPooler = poolerMap?.getCustomerOfUser(userId);
        if (customerOfPooler) {
          resolve(customerOfPooler);
        } else {
          resolve(undefined);
        }
      });
      this.readyEmploymentPoolerCustomerMap$
        .pipe(take(1))
        .subscribe((employmentPoolerMap) => {
          const customerOfPooler =
            employmentPoolerMap?.getCustomerOfUser(userId);
          if (customerOfPooler) {
            resolve(customerOfPooler);
          } else {
            resolve(undefined);
          }
        });
    });
  }

  subscribeSharedPoolCustomers() {
    this.customerService.readyDispoCustomer$.subscribe((customer) => {
      if (this.trackers.sharedPoolCustomers) {
        this.trackers.sharedPoolCustomers.stop();
      }
      this.trackers.sharedPoolCustomers = Tracker.autorun(() => {
        this.sharedPoolCustomers$.next(
          Customers.find({
            $or: [
              {
                'pool.jobsSharedWithCustomerIds': customer?._id,
              },
              {
                'pool.employmentsSharedWithCustomerIds': customer?._id,
              },
            ],
          }).fetch(),
        );
        this.jobSharedPoolCustomers$.next(
          Customers.find({
            'pool.jobsSharedWithCustomerIds': customer?._id,
          }).fetch(),
        );
        this.employmentSharedPoolCustomers$.next(
          Customers.find({
            'pool.employmentsSharedWithCustomerIds': customer?._id,
          }).fetch(),
        );
      });
    });
  }

  subscribePoolers() {
    combineLatest([
      this.customerService.readyDispoCustomer$,
      this.readySharedPoolCustomers$,
    ]).subscribe(([dispoCustomer, sharedPoolCustomers]) => {
      const sharedPriorityMap = dispoCustomer.sharedPoolPriorities?.map(
        (customer) => {
          return customer.customerId;
        },
      );
      if (sharedPriorityMap && sharedPriorityMap.length) {
        sharedPoolCustomers.sort((a, b) => {
          const indexOfA = sharedPriorityMap.indexOf(a._id);
          const indexOfB = sharedPriorityMap.indexOf(b._id);
          if (indexOfA === -1) {
            if (indexOfB === -1) {
              return 0;
            }
            return 1;
          }
          if (indexOfB === -1) {
            return -1;
          }
          return indexOfA - indexOfB;
        });
      }
      this.poolerCustomerMap$.next(
        new PoolerCustomerMap([dispoCustomer, ...sharedPoolCustomers]),
      );

      this.employmentPoolerCustomerMap$.next(
        new PoolerCustomerMap(
          [dispoCustomer, ...sharedPoolCustomers].filter(
            (customer) => customer.enableEmployments,
          ),
        ),
      );
    });
  }

  getPoolers(query: Mongo.Selector<User>, customer?: Customer) {
    const sort = customer?.prioritizeEmployeeLastName
      ? { lastName: 1 }
      : { firstName: 1 };
    return Users.find(
      {
        role: RoleEnum.POOL,
        ...query,
      },
      { sort },
    ) as ObservableCursor<PoolerUser>;
  }
}
